import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Download, Trash2, Pencil, Receipt } from "lucide-react";
import { useApi } from "../hooks/useApi";
import api, { formatError } from "../lib/api";
import { downloadPdf, FileUpload } from "./FileUpload";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "./ui/select";

const EMPTY = { date: new Date().toISOString().slice(0, 10), amount: "", category: "", paid_from: "Bank", payee: "", memo: "", receipt_url: "" };
const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

function ChoiceField({ label, value, options, onChange, testId, placeholder }) {
  return (
    <div><Label>{label}</Label>
      <Select value={value} onValueChange={onChange}><SelectTrigger data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
        <SelectContent>{(options || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
  );
}

function useExpenseForm(initial, cats, open) {
  const [f, setF] = useState(initial || EMPTY);
  React.useEffect(() => { setF(initial || { ...EMPTY, category: cats.categories?.[0] || "" }); }, [initial, open, cats]);
  const set = (k) => (v) => setF((prev) => ({ ...prev, [k]: v?.target ? v.target.value : v }));
  return { f, set };
}

async function saveExpense(f, initial) {
  const body = { ...f, amount: Number(f.amount) };
  if (initial?.id) return api.put(`/admin/finance/expenses/${initial.id}`, body);
  return api.post("/admin/finance/expenses", body);
}

function ExpenseDialog({ open, onClose, initial, cats, onSaved }) {
  const { f, set } = useExpenseForm(initial, cats, open);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await saveExpense(f, initial); toast.success("Expense saved"); onSaved(); onClose(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const isEdit = !!initial?.id;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto" data-testid="expense-dialog">
        <DialogHeader><DialogTitle className="font-serif-display text-crimson">{isEdit ? "Edit expense" : "Record expense"}</DialogTitle></DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <div><Label>Date</Label><Input type="date" value={f.date} onChange={set("date")} data-testid="exp-date" /></div>
          <div><Label>Amount ($)</Label><Input type="number" min="0.01" step="0.01" value={f.amount} onChange={set("amount")} data-testid="exp-amount" /></div>
          <ChoiceField label="Category" value={f.category} options={cats.categories} onChange={set("category")} testId="exp-category" placeholder="Choose…" />
          <ChoiceField label="Paid from" value={f.paid_from} options={cats.sources} onChange={set("paid_from")} testId="exp-source" />
          <div className="sm:col-span-2"><Label>Payee</Label><Input value={f.payee} onChange={set("payee")} placeholder="Who was paid" data-testid="exp-payee" /></div>
          <div className="sm:col-span-2"><Label>Memo</Label><Textarea rows={2} value={f.memo} onChange={set("memo")} data-testid="exp-memo" /></div>
          <div className="sm:col-span-2"><Label>Receipt (photo or PDF)</Label>
            <FileUpload purpose="expense_receipt" label={f.receipt_url ? "Replace receipt" : "Upload receipt"} testId="exp-receipt" onUploaded={(d) => set("receipt_url")(d.url)} />
            {f.receipt_url && <p className="mt-1 text-xs text-palm">Receipt attached</p>}</div>
        </div>
        <DialogFooter><Button onClick={save} disabled={busy || !f.amount || !f.category || !f.date} className="bg-crimson" data-testid="exp-save">{isEdit ? "Save changes" : "Record expense"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CategoryEditor({ cats, onSaved }) {
  const [txt, setTxt] = useState((cats.categories || []).join("\n"));
  const save = async () => { await api.put("/admin/finance/categories", { categories: txt.split("\n") }); toast.success("Categories saved"); onSaved(); };
  return (
    <details className="text-xs" data-testid="expense-categories-editor"><summary className="cursor-pointer text-crimson underline">Edit category list</summary>
      <Textarea rows={6} value={txt} onChange={(e) => setTxt(e.target.value)} className="mt-2" data-testid="exp-cats-text" />
      <Button size="sm" onClick={save} className="mt-2 bg-crimson" data-testid="exp-cats-save">Save categories</Button></details>
  );
}

function ExpenseRow({ e, canEdit, onEdit, onVoid }) {
  return (
    <tr className="border-t border-gold/15" data-testid={`expense-row-${e.id}`}>
      <td className="p-3 font-mono-accent text-xs">{e.date}</td>
      <td className="p-3">{e.category}{e.source === "welfare" && <span className="ml-2 rounded-full bg-palm/15 px-2 py-0.5 text-[10px] font-semibold uppercase text-palm">auto</span>}</td>
      <td className="p-3">{e.payee}<div className="text-xs text-muted-foreground">{e.memo}</div></td>
      <td className="p-3">{e.paid_from}</td>
      <td className="p-3 text-right font-mono-accent">{money(e.amount)}</td>
      <td className="p-3 text-right">
        {e.receipt_url && <button onClick={() => downloadPdf(e.receipt_url.replace(/^\/api/, ""), "receipt")} className="mr-2 text-palm" title="Receipt" data-testid={`expense-receipt-${e.id}`}><Receipt size={14} /></button>}
        {canEdit && e.source === "manual" && <><button onClick={() => onEdit(e)} className="mr-2 text-crimson" data-testid={`expense-edit-${e.id}`}><Pencil size={14} /></button>
          <button onClick={() => onVoid(e)} className="text-destructive" data-testid={`expense-void-${e.id}`}><Trash2 size={14} /></button></>}
      </td>
    </tr>
  );
}

function ExpenseCard({ e, canEdit, onEdit, onVoid }) {
  return (
    <div className="glass rounded-xl p-3 text-sm" data-testid={`expense-card-${e.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-mono-accent text-xs text-muted-foreground">{e.date} · {e.paid_from}</p>
          <p className="font-medium">{e.category}{e.source === "welfare" && <span className="ml-2 rounded-full bg-palm/15 px-2 py-0.5 text-[10px] font-semibold uppercase text-palm">auto</span>}</p>
          {(e.payee || e.memo) && <p className="text-xs text-muted-foreground">{e.payee}{e.memo && ` — ${e.memo}`}</p>}
        </div>
        <span className="shrink-0 font-mono-accent font-semibold">{money(e.amount)}</span>
      </div>
      {(e.receipt_url || (canEdit && e.source === "manual")) && (
        <div className="mt-2 flex gap-2">
          {e.receipt_url && <Button size="sm" variant="outline" className="h-9 gap-1" onClick={() => downloadPdf(e.receipt_url.replace(/^\/api/, ""), "receipt")}><Receipt size={14} /> Receipt</Button>}
          {canEdit && e.source === "manual" && <><Button size="sm" variant="outline" className="h-9 gap-1" onClick={() => onEdit(e)} data-testid={`expense-edit-m-${e.id}`}><Pencil size={14} /> Edit</Button>
            <Button size="sm" variant="ghost" className="h-9 gap-1 text-destructive" onClick={() => onVoid(e)}><Trash2 size={14} /> Void</Button></>}
        </div>
      )}
    </div>
  );
}

export function ExpenseLedger() {
  const year = new Date().getFullYear();
  const { data, reload } = useApi(`/admin/finance/expenses?year=${year}`);
  const { data: cats, reload: reloadCats } = useApi("/admin/finance/categories");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const canEdit = !!cats?.can_edit;
  const items = useMemo(() => data?.items || [], [data]);
  const voidIt = async (e) => {
    const reason = window.prompt("Reason for voiding this expense (min 5 characters):");
    if (!reason) return;
    try { await api.delete(`/admin/finance/expenses/${e.id}`, { data: { reason } }); toast.success("Expense voided"); reload(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };
  return (
    <section data-testid="expense-ledger">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div><h3 className="font-serif-display text-2xl font-bold text-crimson">Expenses {year}</h3>
          <p className="text-sm text-muted-foreground">Total <b className="font-mono-accent text-foreground" data-testid="expense-total">{money(data?.total)}</b> · welfare payouts included automatically</p></div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" className="gap-1" onClick={() => downloadPdf(`/admin/finance/expenses.csv?year=${year}`, `expenses-${year}.csv`)} data-testid="expense-csv"><Download size={14} /> CSV</Button>
          {canEdit && <Button size="sm" className="gap-1 bg-crimson" onClick={() => { setEditing(null); setOpen(true); }} data-testid="expense-add"><Plus size={14} /> Record expense</Button>}
        </div>
      </div>
      {canEdit && cats && <div className="mb-3"><CategoryEditor cats={cats} onSaved={reloadCats} /></div>}
      <div className="space-y-2 lg:hidden" data-testid="expense-cards">
        {items.map((e) => <ExpenseCard key={e.id} e={e} canEdit={canEdit} onEdit={(x) => { setEditing(x); setOpen(true); }} onVoid={voidIt} />)}
        {items.length === 0 && <p className="glass rounded-xl p-6 text-center text-sm text-muted-foreground">No expenses recorded this year.</p>}
      </div>
      <div className="glass hidden overflow-x-auto rounded-2xl lg:block">
        <table className="w-full min-w-[720px] text-sm" data-testid="expense-table">
          <thead className="bg-crimson text-parchment"><tr><th className="p-3 text-left">Date</th><th className="p-3 text-left">Category</th><th className="p-3 text-left">Payee / memo</th><th className="p-3 text-left">Paid from</th><th className="p-3 text-right">Amount</th><th className="p-3"></th></tr></thead>
          <tbody>{items.map((e) => <ExpenseRow key={e.id} e={e} canEdit={canEdit} onEdit={(x) => { setEditing(x); setOpen(true); }} onVoid={voidIt} />)}
            {items.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-muted-foreground" data-testid="expense-empty">No expenses recorded this year.</td></tr>}</tbody>
        </table>
      </div>
      {cats && <ExpenseDialog open={open} onClose={() => setOpen(false)} initial={editing} cats={cats} onSaved={reload} />}
    </section>
  );
}
